import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useToast } from '../hooks/use-toast';
import { classLevels, subjects } from '../mock';
import { Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    classLevel: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast({
        title: "Anfrage gesendet!",
        description: "Vielen Dank für Ihre Nachricht. Wir melden uns in Kürze bei Ihnen.",
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        classLevel: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Fehler beim Senden",
        description: "Die Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt per E-Mail.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Name */}
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Ihr Name"
            required
            className="transition-all duration-200 focus:ring-2 focus:ring-teal-500"
          />
        </div>

        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email">E-Mail *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="ihre.email@beispiel.de"
            required
            className="transition-all duration-200 focus:ring-2 focus:ring-teal-500"
          />
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <Label htmlFor="phone">Telefon *</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+49 123 456 7890"
            required
            className="transition-all duration-200 focus:ring-2 focus:ring-teal-500"
          />
        </div>

        {/* Class Level */}
        <div className="space-y-2">
          <Label htmlFor="classLevel">Klassenstufe *</Label>
          <Select
            value={formData.classLevel}
            onValueChange={(value) => handleSelectChange('classLevel', value)}
            required
          >
            <SelectTrigger className="transition-all duration-200 focus:ring-2 focus:ring-teal-500">
              <SelectValue placeholder="Bitte wählen" />
            </SelectTrigger>
            <SelectContent>
              {classLevels.map((level) => (
                <SelectItem key={level} value={level}>
                  {level}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Subject */}
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="subject">Gewünschtes Fach *</Label>
          <Select
            value={formData.subject}
            onValueChange={(value) => handleSelectChange('subject', value)}
            required
          >
            <SelectTrigger className="transition-all duration-200 focus:ring-2 focus:ring-teal-500">
              <SelectValue placeholder="Bitte wählen" />
            </SelectTrigger>
            <SelectContent>
              {subjects.map((subject) => (
                <SelectItem key={subject} value={subject}>
                  {subject}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Message */}
      <div className="space-y-2">
        <Label htmlFor="message">Ihre Nachricht *</Label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Teilen Sie uns mit, wie wir Ihnen helfen können. Bei Interesse an BuT-Förderung erwähnen Sie dies bitte hier."
          rows={5}
          required
          className="transition-all duration-200 focus:ring-2 focus:ring-teal-500 resize-none"
        />
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full md:w-auto bg-teal-600 hover:bg-teal-700 text-white transition-all duration-300 hover:shadow-lg hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? (
          'Wird gesendet...'
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" />
            Anfrage senden
          </>
        )}
      </Button>

      <p className="text-sm text-gray-600 mt-4">
        * Pflichtfelder. Ihre Daten werden vertraulich behandelt.
      </p>
    </form>
  );
};

export default ContactForm;
